import React, { useState } from 'react';
import { X, Save, Trash2, ChevronDown } from 'lucide-react';

// Modes de paiement selon la devise du salon
const getPaymentOptions = (rawDevise) => {
  if (rawDevise === 'EUR') return ['Especes', 'Carte'];
  // XOF et USD → options Afrique par défaut
  return ['Especes', 'Wave', 'Orange Money'];
};

const PAYMENT_LABELS = {
  'Especes':      'Espèces',
  'Wave':         'Wave',
  'Orange Money': 'Orange Money',
  'Carte':        'Carte Bancaire',
};

// --- MODALE DE MODIFICATION ---
export const EditTransactionModal = ({ transaction, onClose, onUpdate, currency, employees = [], rawDevise }) => {
  const [amount,          setAmount]          = useState(transaction.montant);
  const [desc,            setDesc]            = useState(transaction.description || "");
  const [methodePaiement, setMethodePaiement] = useState(transaction.methodePaiement || '');
  const [employeeId,      setEmployeeId]      = useState(
    transaction.employeeId?._id || transaction.employeeId || ''
  );

  const paymentOptions = getPaymentOptions(rawDevise);

  const handleSubmit = (e) => {
    e.preventDefault();
    onUpdate(transaction._id, {
      montant:         amount,
      description:     desc,
      methodePaiement: methodePaiement || transaction.methodePaiement,
      employeeId:      employeeId || null,
    });
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[150] flex items-end sm:items-center justify-center p-0 sm:p-4">
      <div className="bg-white rounded-t-[2.5rem] sm:rounded-[2.5rem] w-full max-w-md shadow-2xl animate-in slide-in-from-bottom sm:zoom-in duration-300">
        {/* Pill indicator mobile */}
        <div className="flex justify-center pt-3 sm:hidden">
          <div className="w-10 h-1 bg-slate-200 rounded-full" />
        </div>
        <div className="p-6 sm:p-8">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-black text-slate-800">Corriger</h2>
            <button onClick={onClose} className="p-2 bg-slate-100 rounded-full"><X size={20}/></button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Montant */}
            <div>
              <label className="text-xs font-black text-slate-400 uppercase tracking-widest ml-1">Montant ({currency})</label>
              <input
                type="number" required
                className="w-full mt-1 p-4 bg-slate-50 rounded-2xl outline-none font-black text-xl"
                value={amount} onChange={(e) => setAmount(e.target.value)}
              />
            </div>

            {/* Moyen de paiement */}
            <div>
              <label className="text-xs font-black text-slate-400 uppercase tracking-widest ml-1">Moyen de paiement</label>
              <div className="relative mt-1">
                <select
                  className="w-full p-4 bg-slate-50 rounded-2xl outline-none font-bold text-slate-700 appearance-none cursor-pointer"
                  value={methodePaiement}
                  onChange={(e) => setMethodePaiement(e.target.value)}
                >
                  {paymentOptions.map(opt => (
                    <option key={opt} value={opt}>{PAYMENT_LABELS[opt] || opt}</option>
                  ))}
                </select>
                <ChevronDown size={16} className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
              </div>
            </div>

            {/* Employé */}
            {employees.length > 0 && (
              <div>
                <label className="text-xs font-black text-slate-400 uppercase tracking-widest ml-1">Employé</label>
                <div className="relative mt-1">
                  <select
                    className="w-full p-4 bg-slate-50 rounded-2xl outline-none font-bold text-slate-700 appearance-none cursor-pointer"
                    value={employeeId}
                    onChange={(e) => setEmployeeId(e.target.value)}
                  >
                    <option value="">- Admin / Non assigné -</option>
                    {employees.map(emp => (
                      <option key={emp._id} value={emp._id}>
                        {emp.prenom} {emp.nom}
                      </option>
                    ))}
                  </select>
                  <ChevronDown size={16} className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                </div>
              </div>
            )}

            {/* Description */}
            <div>
              <label className="text-xs font-black text-slate-400 uppercase tracking-widest ml-1">Description</label>
              <textarea
                required minLength={5}
                className="w-full mt-1 p-4 bg-slate-50 rounded-2xl outline-none h-24 font-medium"
                placeholder="Pourquoi modifiez-vous cette transaction ?"
                value={desc} onChange={(e) => setDesc(e.target.value)}
              />
            </div>

            <button type="submit" className="w-full p-5 bg-blue-600 text-white rounded-2xl font-black shadow-xl flex items-center justify-center">
              <Save className="mr-2" size={20}/> Enregistrer la correction
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

// --- POP-UP DE SUPPRESSION ---
export const DeleteConfirmPopup = ({ onConfirm, onCancel }) => (
  <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[160] flex items-end sm:items-center justify-center p-0 sm:p-4">
    <div className="bg-white rounded-t-[2.5rem] sm:rounded-[2.5rem] p-8 max-w-sm w-full text-center shadow-2xl animate-in slide-in-from-bottom sm:zoom-in duration-300">
      {/* Pill indicator mobile */}
      <div className="flex justify-center mb-4 sm:hidden">
        <div className="w-10 h-1 bg-slate-200 rounded-full" />
      </div>
      <div className="w-20 h-20 bg-red-50 text-red-500 rounded-3xl flex items-center justify-center mx-auto mb-6 rotate-3">
        <Trash2 size={40} />
      </div>
      <h3 className="text-2xl font-black text-slate-800 mb-2">Supprimer ?</h3>
      <p className="text-slate-500 font-medium mb-8">Cette action est irréversible et sera enregistrée dans les logs.</p>
      <div className="flex space-x-3">
        <button onClick={onCancel} className="flex-1 p-4 bg-slate-100 text-slate-600 rounded-2xl font-black hover:bg-slate-200 transition-all">Annuler</button>
        <button onClick={onConfirm} className="flex-1 p-4 bg-red-500 text-white rounded-2xl font-black shadow-lg shadow-red-100 hover:bg-red-600 transition-all">Supprimer</button>
      </div>
    </div>
  </div>
);